/**
 * Arleo sections: small, independent Web Components.
 * Each component scopes itself to its own section instance so a section can be used more than once.
 */

const define = (name, ctor) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
};

/** Plays a muted, looping video only while it is on screen. */
class ArLazyVideo extends HTMLElement {
  static observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const video = entry.target.querySelector('video');
        if (!video) continue;
        if (entry.isIntersecting) {
          video.preload = 'auto';
          video.play()?.catch(() => {});
        } else {
          video.pause();
        }
      }
    },
    { rootMargin: '200px 0px' }
  );

  connectedCallback() {
    const video = this.querySelector('video');
    if (!video) return;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.removeAttribute('controls');
    ArLazyVideo.observer.observe(this);
  }

  disconnectedCallback() {
    ArLazyVideo.observer.unobserve(this);
  }
}

/** Full-screen viewer for gallery images and the "How does it work" video. */
class ArLightbox extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.media = this.querySelector('[ref="media"]');
    this.cap = this.querySelector('[ref="cap"]');
    this.count = this.querySelector('[ref="count"]');
    try {
      this.items = JSON.parse(this.querySelector('[data-lb-items]')?.textContent || '[]').filter(Boolean);
    } catch {
      this.items = [];
    }
    this.index = 0;

    this.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      if (target.closest('[data-lb-close]') || target === this.dialog) this.close();
      else if (target.closest('[data-lb-prev]')) this.show(this.index - 1);
      else if (target.closest('[data-lb-next]')) this.show(this.index + 1);
    });
    this.dialog?.addEventListener('close', () => {
      this.media?.replaceChildren();
      document.documentElement.style.removeProperty('overflow');
    });
  }

  open(index) {
    if (!this.dialog || !this.items.length) return;
    this.show(index);
    this.dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
  }

  close() {
    this.dialog?.close();
  }

  show(index) {
    const total = this.items.length;
    this.index = (index + total) % total;
    const item = this.items[this.index];
    if (!item || !this.media) return;

    let el;
    if (item.kind === 'video') {
      el = document.createElement('video');
      Object.assign(el, { src: item.src, autoplay: true, muted: true, loop: true, playsInline: true, controls: true });
      el.setAttribute('aria-label', item.alt || '');
    } else {
      el = document.createElement('img');
      Object.assign(el, { src: item.src, alt: item.alt || '' });
    }
    this.media.classList.toggle('ar-v', item.kind === 'video');
    this.media.replaceChildren(el);

    if (this.cap) {
      this.cap.hidden = !item.cap;
      this.cap.textContent = item.cap || '';
    }
    if (this.count) this.count.textContent = `${this.index + 1} / ${total}`;
  }
}

/** Product gallery: native swipe carousel + thumbnails on mobile, opens the lightbox on desktop. */
class ArGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.ar-main');
    this.slides = [...this.querySelectorAll('.ar-main > .ar-tile')];
    this.thumbs = [...this.querySelectorAll('.ar-th')];
    this.current = 0;
    this.lightbox = this.closest('ar-product')?.querySelector('ar-lightbox');

    this.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      const thumb = target.closest('.ar-th');
      if (thumb) return this.go(Number(thumb.dataset.slide));
      const opener = target.closest('[data-lb-index]');
      if (opener) this.lightbox?.open(Number(opener.dataset.lbIndex));
    });

    this.track?.addEventListener('scroll', () => {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = 0;
        const step = this.slides[1] ? this.slides[1].offsetLeft - this.slides[0].offsetLeft : this.track.clientWidth;
        this.setActive(Math.round(this.track.scrollLeft / (step || 1)));
      });
    }, { passive: true });
  }

  /** Scroll the mobile track to a slide. */
  go(index) {
    const slide = this.slides[index];
    if (!slide || !this.track) return;
    this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    this.setActive(index);
  }

  setActive(index) {
    const i = Math.max(0, Math.min(this.slides.length - 1, index));
    if (i === this.current) return;
    this.current = i;
    this.thumbs.forEach((thumb, k) => {
      thumb.classList.toggle('ar-on', k === i);
      thumb.setAttribute('aria-current', String(k === i));
    });
  }
}

/** Bundle picker + buy button: redirects to CheckoutChamp with the selected variant's coc_id. */
class ArBundleForm extends HTMLElement {
  connectedCallback() {
    this.product = this.closest('ar-product');
    this.cards = [...this.querySelectorAll('.ar-card')];
    this.cta = this.querySelector('[ref="cta"]');
    this.days = this.querySelector('[ref="days"]');

    this.addEventListener('change', (event) => {
      const input = /** @type {HTMLInputElement} */ (event.target);
      if (input.type === 'radio') this.select(input.closest('.ar-card'));
    });
    this.cta?.addEventListener('click', () => this.checkout());

    this.querySelectorAll('[data-days-ahead]').forEach((el) => {
      el.textContent = ArBundleForm.formatDate(Number(el.dataset.daysAhead) || 0);
    });
  }

  get selected() {
    return this.cards.find((card) => card.classList.contains('ar-on')) || this.cards[0];
  }

  select(card) {
    if (!card) return;
    this.cards.forEach((c) => {
      const on = c === card;
      c.classList.toggle('ar-on', on);
      c.classList.toggle('ar-off', !on);
    });
    const { price = '', days = '' } = card.dataset;
    if (this.cta) this.cta.textContent = (this.cta.dataset.label || '').replace('[price]', price);
    if (this.days) this.days.textContent = days;
  }

  checkout() {
    if (this.product?.dataset.checkoutMode === 'onetime') return this.checkoutOnetime();
    const domain = this.product?.dataset.cocDomain || '';
    const cocId = this.selected?.dataset.cocId || '';
    let url = domain + '?products=' + cocId + ':1';
    const utms = window.location.search.replace(/^\?/, '');
    if (utms) url += '&' + utms;
    window.location.href = url;
  }

  /** Shopify one-time purchase: empty the cart, add the selected variant × 1, go to checkout. */
  async checkoutOnetime() {
    const variantId = Number(this.selected?.dataset.variantId);
    if (!variantId || this.busy) return;
    this.busy = true;
    this.cta?.setAttribute('aria-busy', 'true');
    if (this.cta) this.cta.disabled = true;

    const root = window.Shopify?.routes?.root || '/';
    const post = (path, body) =>
      fetch(root + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      }).then((res) => {
        if (!res.ok) throw new Error(`${path} failed (${res.status})`);
        return res.json();
      });

    try {
      await post('cart/clear.js');
      await post('cart/add.js', { items: [{ id: variantId, quantity: 1 }] });
      const utms = window.location.search.replace(/^\?/, '');
      window.location.href = root + 'checkout' + (utms ? '?' + utms : '');
    } catch (error) {
      console.error('[arleo] one-time checkout failed', error);
      this.busy = false;
      this.cta?.removeAttribute('aria-busy');
      if (this.cta) this.cta.disabled = false;
    }
  }

  /** "Monday, 26th" */
  static formatDate(daysAhead) {
    const d = new Date();
    d.setDate(d.getDate() + daysAhead);
    const n = d.getDate();
    const sfx = n % 100 >= 11 && n % 100 <= 13 ? 'th' : { 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th';
    const day = d.toLocaleDateString('en-US', { weekday: 'long' });
    return `${day}, ${n}${sfx}`;
  }
}

/** Timeline: steps fade in and the step at the reading line is highlighted. */
class ArTimeline extends HTMLElement {
  connectedCallback() {
    this.steps = [...this.querySelectorAll('.ar-tst')];
    if (!this.steps.length) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduce && 'IntersectionObserver' in window) {
      (this.closest('.ar-pdp') || this).classList.add('ar-rv');
      this.io = new IntersectionObserver(
        (entries) => {
          entries.forEach((en) => {
            if (en.isIntersecting) {
              en.target.classList.add('ar-in');
              this.io.unobserve(en.target);
            }
          });
        },
        { threshold: 0.25, rootMargin: '0px 0px -8% 0px' }
      );
      this.steps.forEach((el) => this.io.observe(el));
    }
    this.onScroll = () => {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = 0;
        this.highlight();
      });
    };
    // Capture phase: on desktop Horizon scrolls .page-wrapper, not the window.
    document.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.highlight();
  }

  disconnectedCallback() {
    document.removeEventListener('scroll', this.onScroll, { capture: true });
    window.removeEventListener('resize', this.onScroll);
    this.io?.disconnect();
  }

  highlight() {
    const rect = this.getBoundingClientRect();
    const line = window.innerHeight * 0.55;
    let current = -1;
    if (rect.bottom > 0 && rect.top < window.innerHeight) {
      this.steps.forEach((el, i) => {
        const node = el.querySelector('.ar-tnode') || el;
        if (node.getBoundingClientRect().top < line) current = i;
      });
    }
    this.steps.forEach((el, i) => el.classList.toggle('ar-lit', i === current));

    // Progress line fills down the rail up to the reading line.
    const progress = Math.max(0, Math.min(line - rect.top - 14, rect.height));
    this.style.setProperty('--ar-tl-progress', `${Math.round(progress)}px`);
  }
}

/**
 * Ring stats ([data-count-item]): each item fades up, its ring fills and its number counts up
 * once it is fully on screen. Items that arrive together are queued so they play one by one.
 */
class ArCount extends HTMLElement {
  static stagger = 300;

  connectedCallback() {
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.items = [...this.querySelectorAll('[data-count-item]')];
    if (!this.items.length) return;
    this.items.forEach((item) => {
      const fill = item.querySelector('[data-dash]');
      if (fill) fill.style.strokeDasharray = `0 ${fill.dataset.circ}`;
      const bar = item.querySelector('[data-width]');
      if (bar) bar.style.width = '0%';
      const num = item.querySelector('[data-count]');
      if (num) {
        num.dataset.final = num.textContent;
        num.textContent = num.textContent.replace(/\d+(\.\d+)?/, '0');
      }
    });
    this.classList.add('ar-anim');
    this.nextStart = 0;
    this.io = new IntersectionObserver(
      (entries) => {
        entries
          .filter((e) => e.isIntersecting)
          .sort((x, y) => this.items.indexOf(x.target) - this.items.indexOf(y.target))
          .forEach((e) => {
            this.io.unobserve(e.target);
            this.queue(e.target);
          });
      },
      // Fully visible and clear of the bottom 20% of the screen.
      { threshold: 1, rootMargin: '0px 0px -20% 0px' }
    );
    this.items.forEach((item) => this.io.observe(item));
  }

  disconnectedCallback() {
    this.io?.disconnect();
  }

  queue(item) {
    const now = performance.now();
    const start = Math.max(now + 250, this.nextStart);
    this.nextStart = start + (Number(this.dataset.stagger) || ArCount.stagger);
    setTimeout(() => this.play(item), start - now);
  }

  play(item) {
    const duration = 1400;
    item.classList.add('ar-in');
    const fill = item.querySelector('[data-dash]');
    if (fill) fill.style.strokeDasharray = `${fill.dataset.dash} ${fill.dataset.circ}`;
    const bar = item.querySelector('[data-width]');
    if (bar) bar.style.width = `${bar.dataset.width}%`;
    const num = item.querySelector('[data-count]');
    const match = num?.dataset.final?.match(/\d+(\.\d+)?/);
    if (!num || !match) return;
    const target = parseFloat(match[0]);
    const decimals = match[1] ? match[1].length - 1 : 0;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      num.textContent = num.dataset.final.replace(match[0], (target * eased).toFixed(decimals));
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }
}

/** Review tag filters + "Load more". */
class ArReviews extends HTMLElement {
  connectedCallback() {
    this.cards = [...this.querySelectorAll('.ar-rcard')];
    this.more = this.querySelector('.ar-more');
    this.initial = Number(this.dataset.initial) || 4;
    this.filter = '';
    this.expanded = false;

    this.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      const chip = target.closest('[data-filter]');
      if (chip) {
        this.filter = chip.dataset.filter || '';
        this.expanded = false;
        this.querySelectorAll('[data-filter]').forEach((c) => {
          const on = c === chip;
          c.classList.toggle('ar-on', on);
          c.setAttribute('aria-pressed', String(on));
        });
        this.render();
      } else if (target.closest('[data-load-more]')) {
        this.expanded = true;
        this.render();
      }
    });
  }

  render() {
    let shown = 0;
    let matching = 0;
    this.cards.forEach((card) => {
      const match = !this.filter || card.dataset.tag === this.filter;
      card.hidden = !match;
      if (!match) return;
      matching++;
      card.classList.toggle('ar-rx', !this.expanded && shown >= this.initial);
      shown++;
    });
    if (this.more) this.more.hidden = this.expanded || matching <= this.initial;
  }
}

/** Horizontal story videos with prev/next, counter and progress bar. */
class ArStories extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[ref="track"]');
    this.count = this.querySelector('[ref="count"]');
    this.bar = this.querySelector('[ref="bar"]');
    if (!this.track) return;
    this.total = this.track.children.length;

    this.addEventListener('click', (event) => {
      const btn = /** @type {HTMLElement} */ (event.target).closest('[data-dir]');
      if (!btn) return;
      this.track.scrollBy({ left: Number(btn.dataset.dir) * this.step(), behavior: 'smooth' });
    });
    this.track.addEventListener('scroll', () => this.update(), { passive: true });
    this.update();
  }

  step() {
    const first = /** @type {HTMLElement} */ (this.track.firstElementChild);
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 14;
    return first ? first.offsetWidth + gap : 0;
  }

  update() {
    const { scrollLeft, clientWidth, scrollWidth } = this.track;
    const step = this.step() || 1;
    const idx = Math.min(this.total - 1, Math.round(scrollLeft / step));
    if (this.count) this.count.textContent = `${idx + 1} / ${this.total}`;
    if (this.bar) this.bar.style.width = `${Math.round(((scrollLeft + clientWidth) / scrollWidth) * 100)}%`;
  }
}

/** Header: mobile menu dialog. */
class ArHeader extends HTMLElement {
  connectedCallback() {
    this.menu = this.querySelector('dialog');
    this.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      const cart = target.closest('[data-ar-cart]');
      if (cart) {
        const form = document.querySelector('ar-bundle-form');
        if (form && typeof form.checkout === 'function') {
          event.preventDefault();
          form.checkout();
        }
        return;
      }
      if (target.closest('[data-menu-open]')) this.menu?.showModal();
      else if (target.closest('[data-menu-close]') || target === this.menu) this.menu?.close();
    });
  }
}

/** Wrapper for one buy-fold section instance. */
class ArProduct extends HTMLElement {}

define('ar-lazy-video', ArLazyVideo);
define('ar-lightbox', ArLightbox);
define('ar-gallery', ArGallery);
define('ar-bundle-form', ArBundleForm);
define('ar-timeline', ArTimeline);
define('ar-count', ArCount);
define('ar-reviews', ArReviews);
define('ar-stories', ArStories);
define('ar-header', ArHeader);
define('ar-product', ArProduct);
